import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/api";
import { useTour } from "../tour/TourProvider";

/**
 * The getting-started checklist a club admin meets on their first visit.
 *
 * The steps come from the server, derived from the club's real data — sports,
 * then coaches, then teams, then athletes, which is the order the data depends
 * on. Nothing is ticked locally, so the card cannot disagree with the club.
 *
 * It disappears on its own once every step is done, and an admin can dismiss it
 * before then. Dismissal is club-wide, because the work is the club's.
 */

export type OnboardingStep = {
  key: string;
  title: string;
  description: string;
  cta: string;
  path: string;
  count: number;
  done: boolean;
};

export type OnboardingState = {
  steps: OnboardingStep[];
  completed: number;
  total: number;
  all_done: boolean;
  dismissed: boolean;
  next_step: string | null;
};

export default function ClubOnboarding({ clubId }: { clubId: string | null | undefined }) {
  const navigate = useNavigate();
  const tour = useTour();
  const [state, setState] = useState<OnboardingState | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!clubId) return;
    try {
      const { data } = await api.get<OnboardingState>(`/clubs/${clubId}/onboarding`);
      setState(data);
    } catch {
      // Non-fatal: the dashboard is still usable without the checklist.
      setState(null);
    }
  }, [clubId]);

  useEffect(() => {
    load();
  }, [load]);

  const dismiss = async () => {
    if (!clubId) return;
    setBusy(true);
    try {
      await api.post(`/clubs/${clubId}/onboarding/dismiss`);
      setState((s) => (s ? { ...s, dismissed: true } : s));
    } catch {
      /* leave it on screen rather than pretending it went */
    }
    setBusy(false);
  };

  // Hidden once finished or dismissed. Finished is not "congratulations,
  // forever" — it is simply no longer the thing to look at.
  if (!state || state.dismissed || state.all_done) return null;

  const pct = Math.round((state.completed / state.total) * 100);

  return (
    <div className="p-5 border shadow-sm bg-white border-indigo-200 rounded-2xl">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-bold text-slate-800">Primii pași cu clubul tău</h2>
          <p className="mt-0.5 text-sm text-slate-500">
            Patru pași și clubul e gata de folosit. Poți sări peste oricare și reveni mai târziu.
          </p>
        </div>
        <div className="flex items-center gap-2 ml-auto shrink-0">
          <button
            type="button"
            onClick={() => tour.start()}
            className="px-3 py-1.5 text-xs font-semibold border rounded-lg border-slate-200 text-slate-600 hover:bg-slate-50"
          >
            Tur ghidat
          </button>
          <button
            type="button"
            onClick={dismiss}
            disabled={busy}
            className="px-3 py-1.5 text-xs font-semibold text-slate-400 rounded-lg hover:text-slate-600 disabled:opacity-60"
          >
            Ascunde
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <div className="flex-1 h-2 overflow-hidden rounded-full bg-slate-100">
          <div className="h-full transition-all bg-indigo-500 rounded-full" style={{ width: `${pct}%` }} />
        </div>
        <span className="text-xs font-semibold tabular-nums text-slate-500">
          {state.completed}/{state.total}
        </span>
      </div>

      <ol className="mt-4 space-y-2">
        {state.steps.map((step, i) => {
          // Exactly one step is highlighted: the first unfinished one. A list
          // where everything shouts is a list nobody reads.
          const isNext = step.key === state.next_step;
          return (
            <li
              key={step.key}
              className={`flex items-start gap-3 p-3 rounded-xl border transition ${
                step.done
                  ? "border-transparent bg-slate-50"
                  : isNext
                    ? "border-indigo-300 bg-indigo-50/60"
                    : "border-slate-100 bg-white"
              }`}
            >
              <span
                className={`flex items-center justify-center w-6 h-6 text-xs font-bold rounded-full shrink-0 ${
                  step.done ? "bg-emerald-500 text-white" : isNext ? "bg-indigo-600 text-white" : "bg-slate-200 text-slate-500"
                }`}
                aria-hidden
              >
                {step.done ? "✓" : i + 1}
              </span>

              <div className="min-w-0 flex-1">
                <p className={`text-sm font-semibold ${step.done ? "text-slate-500 line-through" : "text-slate-800"}`}>
                  {step.title}
                </p>
                {!step.done && <p className="mt-0.5 text-xs text-slate-500">{step.description}</p>}
                {step.done && step.count > 0 && (
                  <p className="mt-0.5 text-xs text-emerald-600">
                    {step.count} {step.key === "sports" ? "sporturi" : step.key === "coaches" ? "antrenori" : step.key === "teams" ? "echipe" : "sportivi"}
                  </p>
                )}
              </div>

              {!step.done && (
                <button
                  type="button"
                  onClick={() => navigate(step.path)}
                  className={`px-3 py-1.5 text-xs font-semibold rounded-lg shrink-0 ${
                    isNext
                      ? "bg-indigo-600 text-white hover:bg-indigo-700"
                      : "border border-slate-200 text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {step.cta}
                </button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
